<script lang="ts">
  import { link, push } from 'svelte-spa-router';
  import { location } from '../../framework/js/spaLocation';
  import { onMount } from 'svelte';
  import { api } from '../../framework/js/api.js';
  import AssessmentList from './AssessmentList.svelte';
  import AssessmentView from './AssessmentView.svelte';
  import AssessmentWizard from './AssessmentWizard.svelte';
  import FlowModal from '../../framework/components/FlowModal.svelte';
  import { AppPageHeader, AgentHero } from '../../framework/components/app';

  type AssessmentModalMode = { kind: 'new' } | { kind: 'edit'; id: string };

  let assessmentId: string | null = null;
  let hasItems: boolean | null = null;
  let totalCount: number | null = null;

  $: {
    const path = $location || '';
    if (/\/assessments\/[^/]+\/edit$/.test(path)) {
      assessmentId = null;
    } else {
      const match = path.match(/^\/assessments\/([^/]+)$/);
      if (match && match[1] && match[1] !== 'new') {
        assessmentId = match[1];
      } else {
        assessmentId = null;
      }
    }
  }

  $: modalMode = ((): AssessmentModalMode | null => {
    const path = $location || '';
    if (path === '/assessments/new') return { kind: 'new' as const };
    const m = path.match(/^\/assessments\/([^/]+)\/edit$/);
    if (m?.[1]) return { kind: 'edit' as const, id: m[1] };
    return null;
  })();

  function handleModalClose() {
    if (modalMode?.kind === 'edit') {
      push(`/assessments/${modalMode.id}`);
    } else {
      push('/assessments');
    }
  }

  async function probe() {
    try {
      const res: any = await api.getAssessments('', 'updated', 'desc');
      if (res?.success && res?.data?.list) {
        const count = res.data.list.length;
        totalCount = count;
        hasItems = count > 0;
      } else {
        hasItems = false;
      }
    } catch {
      hasItems = false;
    }
  }

  onMount(() => {
    if (!assessmentId) probe();
  });
</script>

<div class="assessments-page" class:assessments-page--detail={!!assessmentId}>
  {#if assessmentId}
    {#key assessmentId}
      <AssessmentView {assessmentId} />
    {/key}
  {:else if hasItems === null}
    <!-- probing -->
  {:else if hasItems}
    <AppPageHeader
      title="Illuminator"
      subtitle="Do you even need primary research?"
      actionLabel="New assessment"
      actionHref="/assessments/new"
    />
    <AssessmentList />
  {:else}
    <AgentHero
      title="Illuminator"
      subtitle="Do you even need primary research? Run a quick assessment and find out in minutes."
      features={[
        'Synthesises existing evidence across your decision context',
        'Returns a verdict with confidence level',
        'Saves weeks of unnecessary fieldwork',
      ]}
      historyHref="/assessments"
      historyLabel="View past assessments"
      historyCount={totalCount}
    >
      <div slot="creator" class="creator-card">
        <h2 class="creator-title">Start a new assessment</h2>
        <p class="creator-sub">Answer six short prompts. We do the rest.</p>
        <a use:link href="/assessments/new" class="creator-cta">
          <span>Start assessment</span>
          <i class="angle right icon" aria-hidden="true"></i>
        </a>
      </div>
    </AgentHero>
  {/if}
</div>

{#if modalMode}
  <FlowModal on:close={handleModalClose}>
    {#if modalMode.kind === 'new'}
      <AssessmentWizard />
    {:else}
      <AssessmentWizard editAssessmentId={modalMode.id} />
    {/if}
  </FlowModal>
{/if}

<style>
  .assessments-page {
    padding: var(--space-5) var(--space-6);
    min-height: 0;
  }

  /* Detail view scrolls in the dashboard content pane — avoid nested height traps */
  .assessments-page--detail {
    padding-bottom: 0;
  }

  @media (max-width: 768px) {
    .assessments-page { padding: var(--space-4); }
    .assessments-page--detail { padding-bottom: 0; }
  }

  .creator-card {
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-pop);
    padding: var(--space-6);
    text-align: center;
  }

  .creator-title {
    font-size: var(--text-xl);
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 var(--space-2) 0;
  }

  .creator-sub {
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    margin: 0 0 var(--space-5) 0;
  }

  .creator-cta {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-6);
    background: var(--primary);
    color: var(--white);
    border-radius: var(--radius-cta);
    text-decoration: none;
    font-weight: 500;
    box-shadow: var(--shadow-cta);
    transition: filter .15s;
  }

  .creator-cta:hover { filter: brightness(.95); color: var(--white); }
  .creator-cta:focus-visible { outline: none; box-shadow: var(--focus-ring); }
</style>
